import { supabase } from '@/lib/supabase'
import type { Invoice, InvoiceFormData, InvoiceFilters, CreateInvoiceData, InvoiceStats, InvoiceLine, ScheduledReminder, ScheduledReminderKind } from '@/types/invoice'
import { emailService } from './email'
import { createFactureEnvoiEmail, createPaymentThankYouEmail, createRelanceEmail } from '@/utils/emailTemplates'
import { savePdfToProjectDocuments } from './projectDocuments'
import { qontoApiCall, getQontoStatus, downloadQontoAttachment } from './qontoApi'
import { buildQontoClientBody } from './contacts'

export const invoicesService = {
  async getAll(filters?: InvoiceFilters): Promise<{ invoices: Invoice[]; total: number }> {
    let query = supabase
      .from('facture')
      .select(`
        *,
        client:users!utilisateur_id(id, full_name, email, company),
        projet:projects!projet_id(id, title)
      `, { count: 'exact' })
      .order(filters?.sortBy ?? 'date_creation', { ascending: filters?.sortOrder === 'asc', nullsFirst: false })

    if (filters?.search) {
      query = query.ilike('numero', `%${filters.search}%`)
    }

    if (filters?.statut) {
      query = query.eq('statut', filters.statut)
    }

    if (filters?.clientId) {
      query = query.eq('utilisateur_id', filters.clientId)
    }

    if (filters?.dateFrom) {
      query = query.gte('date_creation', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('date_creation', filters.dateTo)
    }

    // Pagination
    const page = filters?.page ?? 1
    const limit = filters?.limit ?? 20
    const from = (page - 1) * limit
    const to = from + limit - 1
    query = query.range(from, to)

    const { data, error, count } = await query

    if (error) throw error
    return { invoices: data as Invoice[], total: count ?? 0 }
  },

  async getById(id: string): Promise<Invoice | null> {
    const { data, error } = await supabase
      .from('facture')
      .select(`
        *,
        client:users!utilisateur_id(*),
        projet:projects!projet_id(*)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as Invoice
  },

  async getStats(): Promise<InvoiceStats> {
    const { data, error } = await supabase
      .from('facture')
      .select('statut, total_ttc')

    if (error) throw error

    const invoices = data || []

    return {
      total: invoices.reduce((sum, f) => sum + (f.total_ttc || 0), 0),
      payees: invoices.filter(f => f.statut === 'payee').reduce((sum, f) => sum + (f.total_ttc || 0), 0),
      enAttente: invoices.filter(f => f.statut === 'en_attente' || f.statut === 'sent').reduce((sum, f) => sum + (f.total_ttc || 0), 0),
      enRetard: invoices.filter(f => f.statut === 'overdue').reduce((sum, f) => sum + (f.total_ttc || 0), 0),
    }
  },

  async create(data: CreateInvoiceData): Promise<Invoice> {
    // Generate invoice number
    const year = new Date().getFullYear()
    const { count } = await supabase
      .from('facture')
      .select('*', { count: 'exact', head: true })
      .gte('date_creation', `${year}-01-01`)

    const numero = `FAC-${year}-${String((count || 0) + 1).padStart(4, '0')}`

    // Calculate totals
    const total_ht = data.lignes.reduce((sum, l) => sum + l.total_ht, 0)
    const tva = data.lignes.reduce((sum, l) => sum + (l.total_ht * l.taux_tva / 100), 0)
    const total_ttc = total_ht + tva

    // Create invoice
    const { data: invoice, error } = await supabase
      .from('facture')
      .insert({
        numero,
        utilisateur_id: data.utilisateur_id,
        projet_id: data.projet_id || null,
        date_creation: data.date_creation,
        date_echeance: data.date_echeance,
        total_ht,
        tva,
        total_ttc,
        produits_json: data.lignes,
        conditions: data.conditions,
        notes_internes: data.notes_internes || null,
        statut: data.statut === 'sent' ? 'en_attente' : 'draft',
      })
      .select()
      .single()

    if (error) throw error

    const createdInvoice = invoice as Invoice

    // Sync avec Qonto (non bloquant)
    try {
      const qontoStatus = await getQontoStatus()
      if (qontoStatus.connected) {
        // Récupérer le client avec toutes ses données pour Qonto
        const { data: clientData } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.utilisateur_id)
          .single()

        let qontoClientId = (clientData as any)?.qonto_client_id || null

        // Créer le client Qonto si besoin
        if (!qontoClientId && clientData) {
          try {
            const clientBody = buildQontoClientBody(clientData as any)
            const result = await qontoApiCall<{ client: { id: string } }>('/clients', {
              method: 'POST',
              body: clientBody,
            })
            if (result?.client?.id) {
              qontoClientId = result.client.id
              await supabase.from('users').update({ qonto_client_id: result.client.id }).eq('id', data.utilisateur_id)
            }
          } catch (clientErr) {
            console.error('Erreur création client Qonto:', clientErr)
          }
        }

        // Récupérer l'IBAN depuis l'organisation Qonto
        let iban = ''
        try {
          const org = await qontoApiCall<{ organization: { bank_accounts: { iban: string }[] } }>('/organization')
          iban = org?.organization?.bank_accounts?.[0]?.iban || ''
        } catch {
          console.error('Erreur récupération IBAN Qonto')
        }

        if (qontoClientId && iban) {
          // Assurer que le client a locale/currency/billing_address à jour
          if (clientData) {
            await qontoApiCall(`/clients/${qontoClientId}`, {
              method: 'PATCH',
              body: buildQontoClientBody(clientData as any),
            }).catch(() => {})
          }

          const qontoInvoice = await qontoApiCall<{ client_invoice: { id: string; invoice_url: string } }>('/client_invoices', {
            method: 'POST',
            body: {
              client_id: qontoClientId,
              issue_date: data.date_creation,
              due_date: data.date_echeance,
              currency: 'EUR',
              payment_methods: { iban },
              items: data.lignes.map((l: InvoiceLine) => ({
                title: (l.description || 'Article').substring(0, 120),
                quantity: String(l.quantite || 1),
                unit_price: { value: String(l.prix_unitaire || 0), currency: 'EUR' },
                vat_rate: String((l.taux_tva ?? 20) / 100),
                currency: 'EUR',
              })),
            },
          })

          if (qontoInvoice?.client_invoice) {
            await supabase
              .from('facture')
              .update({
                qonto_id: qontoInvoice.client_invoice.id,
                qonto_url: qontoInvoice.client_invoice.invoice_url,
              })
              .eq('id', createdInvoice.id)

            // Mettre à jour l'objet en mémoire pour éviter la génération du PDF local en doublon
            createdInvoice.qonto_id = qontoInvoice.client_invoice.id
            createdInvoice.qonto_url = qontoInvoice.client_invoice.invoice_url

            // Sauvegarder le PDF Qonto dans les documents projet
            // Le PDF est généré de manière asynchrone par Qonto, on retry si attachment_id est null
            if (data.projet_id) {
              try {
                let attachmentId: string | undefined
                for (let attempt = 0; attempt < 3; attempt++) {
                  const invoiceDetail = await qontoApiCall<{ client_invoice: { attachment_id?: string } }>(`/client_invoices/${qontoInvoice.client_invoice.id}`)
                  attachmentId = invoiceDetail?.client_invoice?.attachment_id || undefined
                  if (attachmentId) break
                  // Attendre 2s avant retry
                  await new Promise(resolve => setTimeout(resolve, 2000))
                }
                if (attachmentId) {
                  const pdfBlob = await downloadQontoAttachment(attachmentId)
                  await savePdfToProjectDocuments(
                    data.projet_id,
                    pdfBlob,
                    `Facture ${numero} - Qonto`,
                    'facture',
                    `PDF Qonto de la facture ${numero}`
                  )
                }
              } catch (storageErr) {
                console.error('Erreur sauvegarde PDF Qonto facture:', storageErr)
              }
            }
          }
        }
      }
    } catch (qontoError) {
      console.error('Erreur sync Qonto facture:', qontoError)
    }

    // Envoyer l'email si la facture est envoyée (statut sent ou en_attente)
    if (data.statut === 'sent' || createdInvoice.statut === 'en_attente') {
      try {
        // Récupérer les informations du client
        const { data: client, error: clientError } = await supabase
          .from('users')
          .select('email, full_name')
          .eq('id', data.utilisateur_id)
          .single()

        if (clientError) {
          console.error('Erreur lors de la récupération du client:', clientError)
        } else if (client?.email) {
          const clientName = client.full_name || 'Client'

          // Générer le HTML avec le template
          const htmlContent = createFactureEnvoiEmail({
            clientName,
            factureNumero: numero,
            montantTTC: total_ttc,
            dateEcheance: data.date_echeance,
          })

          // PDF en PJ : uniquement le PDF Qonto si disponible
          let emailAttachments: { filename: string; content: string }[] | undefined
          if (createdInvoice.qonto_id) {
            try {
              const invoiceDetail = await qontoApiCall<{ client_invoice: { attachment_id?: string } }>(`/client_invoices/${createdInvoice.qonto_id}`)
              const attachmentId = invoiceDetail?.client_invoice?.attachment_id
              if (attachmentId) {
                const pdfBlob = await downloadQontoAttachment(attachmentId)
                const buffer = await pdfBlob.arrayBuffer()
                const bytes = new Uint8Array(buffer)
                let binary = ''
                for (let i = 0; i < bytes.length; i++) {
                  binary += String.fromCharCode(bytes[i])
                }
                emailAttachments = [{ filename: `Facture_${numero}.pdf`, content: btoa(binary) }]
              }
            } catch (pdfErr) {
              console.error('Erreur PDF Qonto pour email création facture:', pdfErr)
            }
          }

          const emailRequest = {
            to: client.email,
            toName: clientName,
            subject: `Facture ${numero} - LesSablesWeb`,
            html: htmlContent,
            text: `Votre facture ${numero} d'un montant de ${total_ttc.toFixed(2)}€ TTC est disponible. Date d'échéance : ${new Date(data.date_echeance).toLocaleDateString('fr-FR')}`,
            referenceType: 'facture' as const,
            referenceId: createdInvoice.id,
            attachments: emailAttachments,
          }

          // Envoyer à tous les membres du projet si projet_id existe
          if (data.projet_id) {
            await emailService.sendEmailToProject(data.projet_id, emailRequest)
          } else {
            await emailService.sendEmail(emailRequest)
          }
        }
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi de l\'email de facture:', emailError)
        // Ne pas bloquer la création si l'email échoue
      }
    }

    return createdInvoice
  },

  async update(id: string, data: Partial<InvoiceFormData>): Promise<Invoice> {
    const updateData: Record<string, unknown> = {
      ...data,
      updated_at: new Date().toISOString(),
    }

    // Recalculate totals if lignes are updated
    if (data.produits_json) {
      const lignes = data.produits_json as InvoiceLine[]
      const total_ht = lignes.reduce((sum, l) => sum + l.total_ht, 0)
      const tva = lignes.reduce((sum, l) => sum + (l.total_ht * l.taux_tva / 100), 0)
      updateData.total_ht = total_ht
      updateData.tva = tva
      updateData.total_ttc = total_ht + tva
    }

    const { data: invoice, error } = await supabase
      .from('facture')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return invoice as Invoice
  },

  async updateStatus(id: string, statut: string, date_paiement?: string): Promise<void> {
    const updateData: Record<string, unknown> = {
      statut,
      updated_at: new Date().toISOString(),
    }

    if (date_paiement) {
      updateData.date_paiement = date_paiement
    }

    const { error } = await supabase
      .from('facture')
      .update(updateData)
      .eq('id', id)

    if (error) throw error

    // Log the status change
    await supabase.from('notification_logs').insert({
      type: 'system',
      titre: 'Statut facture modifie',
      message: `Nouveau statut : ${statut}`,
      reference_type: 'facture',
      reference_id: id,
    })
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('facture').delete().eq('id', id)

    if (error) throw error
  },

  async generateNumero(): Promise<string> {
    const year = new Date().getFullYear()
    const { count } = await supabase
      .from('facture')
      .select('*', { count: 'exact', head: true })
      .gte('date_creation', `${year}-01-01`)

    const nextNumber = (count ?? 0) + 1
    return `FAC-${year}-${nextNumber.toString().padStart(4, '0')}`
  },

  async sendReminder(invoiceId: string, userId: string): Promise<void> {
    // Récupérer la facture avec les infos client
    const { data: invoice, error: invoiceError } = await supabase
      .from('facture')
      .select(`
        *,
        client:users!utilisateur_id(id, email, full_name)
      `)
      .eq('id', invoiceId)
      .single()

    if (invoiceError) throw invoiceError
    if (!invoice) throw new Error('Facture non trouvée')

    const typedInvoice = invoice as Invoice & {
      client?: { id: string; email: string; full_name: string | null }
    }

    const clientEmail = typedInvoice.client?.email
    const clientName = typedInvoice.client?.full_name || 'Client'

    if (!clientEmail) {
      throw new Error('Le client n\'a pas d\'adresse email')
    }

    // Calculer si l'échéance est dépassée
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const echeance = new Date(typedInvoice.date_echeance)
    echeance.setHours(0, 0, 0, 0)
    const isOverdue = echeance.getTime() < today.getTime()
    const daysOverdue = Math.floor((today.getTime() - echeance.getTime()) / (1000 * 60 * 60 * 24))
    const dateStr = echeance.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    const montantStr = `${typedInvoice.total_ttc.toFixed(2)}€`

    let headline: string
    let message: string
    let emailSubject: string

    if (isOverdue) {
      headline = `Facture ${typedInvoice.numero} en retard de paiement`
      emailSubject = `Relance — Facture ${typedInvoice.numero} en retard`
      message = `Nous vous informons que votre facture ${typedInvoice.numero} d'un montant de ${montantStr} est arrivée à échéance le ${dateStr} et reste impayée à ce jour (retard de ${daysOverdue} jour${daysOverdue > 1 ? 's' : ''}).\n\nMerci de bien vouloir procéder au règlement dans les plus brefs délais.`
    } else {
      headline = `Rappel — Facture ${typedInvoice.numero} arrivant à échéance`
      emailSubject = `Rappel — Facture ${typedInvoice.numero}`
      message = `Nous vous rappelons que votre facture ${typedInvoice.numero} d'un montant de ${montantStr} arrive à échéance le ${dateStr}.\n\nMerci de procéder à son règlement avant cette date.`
    }

    const htmlContent = createRelanceEmail({
      contactName: clientName,
      subject: headline,
      message,
    })

    const emailRequest = {
      to: clientEmail,
      toName: clientName,
      subject: emailSubject,
      html: htmlContent,
      text: `Bonjour ${clientName},\n\n${message}\n\nCordialement,\nL'équipe Les Sables Web\ncontact@lessablesweb.fr`,
      referenceType: 'facture' as const,
      referenceId: invoiceId,
    }

    const emailResult = await emailService.sendEmail(emailRequest)

    if (!emailResult.success) {
      throw new Error(emailResult.error || 'Erreur lors de l\'envoi de la relance')
    }

    // Log the reminder
    await supabase.from('notification_logs').insert({
      type: 'reminder_sent',
      titre: 'Relance envoyee',
      message: `Relance envoyée à ${clientEmail} pour la facture ${typedInvoice.numero}${isOverdue ? ` (en retard de ${daysOverdue}j)` : ''}`,
      reference_type: 'facture',
      reference_id: invoiceId,
      user_id: userId,
    })
  },

  async sendPaymentThankYou(invoiceId: string, userId: string): Promise<void> {
    const { data: invoice, error: invoiceError } = await supabase
      .from('facture')
      .select(`
        *,
        client:users!utilisateur_id(id, email, full_name)
      `)
      .eq('id', invoiceId)
      .single()

    if (invoiceError) throw invoiceError
    if (!invoice) throw new Error('Facture non trouvée')

    const typedInvoice = invoice as Invoice & {
      client?: { id: string; email: string; full_name: string | null }
    }

    if (typedInvoice.statut !== 'payee') {
      throw new Error('Le remerciement ne peut être envoyé que pour une facture payée')
    }

    const clientEmail = typedInvoice.client?.email
    const clientName = typedInvoice.client?.full_name || 'Client'

    if (!clientEmail) {
      throw new Error('Le client n\'a pas d\'adresse email')
    }

    const reviewUrl = 'https://g.page/r/CVNuwh4e6iPpEAE/review'
    const montantStr = `${typedInvoice.total_ttc.toFixed(2)}€`
    const dateStr = typedInvoice.date_paiement
      ? new Date(typedInvoice.date_paiement).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null

    const htmlContent = createPaymentThankYouEmail({
      clientName,
      factureNumero: typedInvoice.numero,
      montantTTC: typedInvoice.total_ttc,
      datePaiement: typedInvoice.date_paiement,
      reviewUrl,
    })

    const textContent = [
      `Bonjour ${clientName},`,
      '',
      `Nous vous confirmons la bonne réception de votre règlement de ${montantStr} TTC pour la facture ${typedInvoice.numero}${dateStr ? ` (paiement du ${dateStr})` : ''}.`,
      '',
      'Un grand merci pour votre confiance !',
      '',
      'Si notre travail vous a plu, vous pouvez nous laisser un avis Google ici :',
      reviewUrl,
      '',
      'Cordialement,',
      "L'équipe Les Sables Web",
      'contact@lessablesweb.fr',
    ].join('\n')

    const emailResult = await emailService.sendEmail({
      to: clientEmail,
      toName: clientName,
      subject: `Merci pour votre règlement — Facture ${typedInvoice.numero}`,
      html: htmlContent,
      text: textContent,
      referenceType: 'facture',
      referenceId: invoiceId,
    })

    if (!emailResult.success) {
      throw new Error(emailResult.error || 'Erreur lors de l\'envoi du remerciement')
    }

    await supabase.from('notification_logs').insert({
      type: 'payment_thanks_sent',
      titre: 'Remerciement de paiement envoye',
      message: `Email de remerciement envoyé à ${clientEmail} pour la facture ${typedInvoice.numero}`,
      reference_type: 'facture',
      reference_id: invoiceId,
      user_id: userId,
    })
  },

  async scheduleReminder(invoiceId: string, scheduledAt: Date, userId: string): Promise<void> {
    if (scheduledAt.getTime() <= Date.now()) {
      throw new Error('La date de planification doit être dans le futur')
    }

    const { error } = await supabase
      .from('scheduled_invoice_reminders')
      .insert({
        invoice_id: invoiceId,
        scheduled_at: scheduledAt.toISOString(),
        created_by: userId,
        kind: 'reminder',
      })

    if (error) throw error
  },

  async schedulePaymentThankYou(invoiceId: string, scheduledAt: Date, userId: string): Promise<void> {
    if (scheduledAt.getTime() <= Date.now()) {
      throw new Error('La date de planification doit être dans le futur')
    }

    // Garde-fou côté client : on ne planifie un remerciement que pour une facture payée.
    const { data: invoice, error: invErr } = await supabase
      .from('facture')
      .select('statut')
      .eq('id', invoiceId)
      .single()

    if (invErr) throw invErr
    if ((invoice as { statut: string } | null)?.statut !== 'payee') {
      throw new Error('Le remerciement ne peut être planifié que pour une facture payée')
    }

    const { error } = await supabase
      .from('scheduled_invoice_reminders')
      .insert({
        invoice_id: invoiceId,
        scheduled_at: scheduledAt.toISOString(),
        created_by: userId,
        kind: 'thank_you',
      })

    if (error) throw error
  },

  async listScheduledReminders(invoiceId: string, kind?: ScheduledReminderKind): Promise<ScheduledReminder[]> {
    let query = supabase
      .from('scheduled_invoice_reminders')
      .select('id, invoice_id, scheduled_at, status, kind, sent_at, error, attempts, created_at')
      .eq('invoice_id', invoiceId)
      .order('scheduled_at', { ascending: true })

    if (kind) {
      query = query.eq('kind', kind)
    }

    const { data, error } = await query

    if (error) throw error
    return (data || []) as ScheduledReminder[]
  },

  async listPendingByInvoiceIds(
    invoiceIds: string[],
    kind?: ScheduledReminderKind
  ): Promise<Record<string, number>> {
    if (invoiceIds.length === 0) return {}

    let query = supabase
      .from('scheduled_invoice_reminders')
      .select('invoice_id')
      .in('invoice_id', invoiceIds)
      .eq('status', 'pending')

    if (kind) {
      query = query.eq('kind', kind)
    }

    const { data, error } = await query

    if (error) throw error

    const counts: Record<string, number> = {}
    for (const row of (data || []) as { invoice_id: string }[]) {
      counts[row.invoice_id] = (counts[row.invoice_id] ?? 0) + 1
    }
    return counts
  },

  async cancelScheduledReminder(id: string): Promise<void> {
    const { error } = await supabase
      .from('scheduled_invoice_reminders')
      .update({ status: 'cancelled' })
      .eq('id', id)
      .eq('status', 'pending')

    if (error) throw error
  },

  async sendInvoice(invoiceId: string): Promise<void> {
    // Récupérer la facture avec les infos client
    const { data: invoice, error: invoiceError } = await supabase
      .from('facture')
      .select(`
        *,
        client:users!utilisateur_id(id, email, full_name)
      `)
      .eq('id', invoiceId)
      .single()

    if (invoiceError) throw invoiceError
    if (!invoice) throw new Error('Facture non trouvée')

    const typedInvoice = invoice as Invoice & {
      client?: { id: string; email: string; full_name: string | null }
    }

    const clientEmail = typedInvoice.client?.email
    const clientName = typedInvoice.client?.full_name || 'Client'

    if (!clientEmail) {
      throw new Error('Le client n\'a pas d\'adresse email')
    }

    // Générer le HTML avec le template
    const htmlContent = createFactureEnvoiEmail({
      clientName,
      factureNumero: typedInvoice.numero,
      montantTTC: typedInvoice.total_ttc,
      dateEcheance: typedInvoice.date_echeance,
    })

    // PDF en PJ : uniquement le PDF Qonto si disponible
    let attachments: { filename: string; content: string }[] | undefined
    if (typedInvoice.qonto_id) {
      try {
        const qontoInv = await qontoApiCall<{ client_invoice: { attachment_id: string } }>(`/client_invoices/${typedInvoice.qonto_id}`)
        if (qontoInv?.client_invoice?.attachment_id) {
          const pdfBlob = await downloadQontoAttachment(qontoInv.client_invoice.attachment_id)
          const buffer = await pdfBlob.arrayBuffer()
          const bytes = new Uint8Array(buffer)
          let binary = ''
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i])
          }
          const base64 = btoa(binary)
          attachments = [{
            filename: `Facture_${typedInvoice.numero}.pdf`,
            content: base64,
          }]
        }
      } catch (pdfErr) {
        console.error('Erreur récupération PDF Qonto pour email facture:', pdfErr)
      }
    }

    // Envoyer l'email à tous les membres du projet
    const emailRequest = {
      to: clientEmail,
      toName: clientName,
      subject: `Facture ${typedInvoice.numero} - LesSablesWeb`,
      html: htmlContent,
      text: `Votre facture ${typedInvoice.numero} d'un montant de ${typedInvoice.total_ttc.toFixed(2)}€ TTC est disponible. Date d'échéance : ${new Date(typedInvoice.date_echeance).toLocaleDateString('fr-FR')}`,
      referenceType: 'facture' as const,
      referenceId: invoiceId,
      attachments,
    }

    const emailResult = typedInvoice.projet_id
      ? (await emailService.sendEmailToProject(typedInvoice.projet_id, emailRequest)).mainResult
      : await emailService.sendEmail(emailRequest)

    if (!emailResult.success) {
      throw new Error(emailResult.error || 'Erreur lors de l\'envoi de l\'email')
    }

    // Mettre à jour le statut si c'est un brouillon
    if (typedInvoice.statut === 'draft') {
      await this.updateStatus(invoiceId, 'en_attente')
    }

    // Sauvegarder le PDF Qonto dans les documents projet (si pas déjà fait)
    if (typedInvoice.qonto_id && typedInvoice.projet_id) {
      try {
        const { data: existingDoc } = await supabase
          .from('project_documents')
          .select('id')
          .eq('project_id', typedInvoice.projet_id)
          .ilike('nom', `%${typedInvoice.numero}%Qonto%`)
          .maybeSingle()

        if (!existingDoc) {
          const qontoInv = await qontoApiCall<{ client_invoice: { attachment_id?: string } }>(`/client_invoices/${typedInvoice.qonto_id}`)
          const attachmentId = qontoInv?.client_invoice?.attachment_id
          if (attachmentId) {
            const pdfBlob = await downloadQontoAttachment(attachmentId)
            await savePdfToProjectDocuments(
              typedInvoice.projet_id,
              pdfBlob,
              `Facture ${typedInvoice.numero} - Qonto`,
              'facture',
              `PDF Qonto de la facture ${typedInvoice.numero}`
            )
          }
        }
      } catch (docErr) {
        console.error('Erreur sauvegarde PDF Qonto facture dans documents projet:', docErr)
      }
    }

    // Logger l'envoi
    await supabase.from('notification_logs').insert({
      type: 'invoice_sent',
      titre: 'Facture envoyée',
      message: `La facture ${typedInvoice.numero} a été envoyée à ${clientEmail}`,
      reference_type: 'facture',
      reference_id: invoiceId,
    })
  },
}
